import React, { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useIsMobile } from '../hooks/useIsMobile';
type ParticleKind = 'electron' | 'proton' | 'molecule';
interface Particle {
  id: number;
  kind: ParticleKind;
  size: number;
  startX: number;
  endX: number;
  duration: number;
  delay: number;
  opacity: number;
  color: string;
  spin: number;
}
const COLORS = ['#2350C8', '#5B8DEF', '#8B7BE8'];

/** Cząstki poruszają się wyłącznie po marginesach — środkowe 60% ekranu zostaje wolne dla treści. */
function buildParticles(count: number, mobile: boolean): Particle[] {
  const edge = mobile ? 14 : 20;
  const maxOpacity = mobile ? 0.11 : 0.16;
  const minOpacity = 0.08;
  return Array.from({
    length: count
  }, (_, i) => {
    const fromLeft = i % 2 === 0;
    const kinds: ParticleKind[] = ['electron', 'proton', 'molecule'];
    const rand = (min: number, max: number) => min + Math.random() * (max - min);
    const startX = fromLeft ? rand(1, edge) : rand(100 - edge, 98);
    const endX = fromLeft ? rand(100 - edge, 98) : rand(1, edge);
    return {
      id: i,
      kind: kinds[i % 3],
      size: mobile ? rand(18, 30) : rand(24, 48),
      startX,
      endX,
      duration: rand(14, 26),
      delay: rand(0, 12),
      opacity: rand(minOpacity, maxOpacity),
      color: COLORS[i % COLORS.length],
      spin: fromLeft ? 1 : -1
    };
  });
}
function ParticleGlyph({
  kind,
  size,
  color
}: {
  kind: ParticleKind;
  size: number;
  color: string;
}) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 48 48',
    fill: 'none',
    stroke: color,
    strokeWidth: 1.4,
    'aria-hidden': true as const
  };
  if (kind === 'electron') {
    return <svg {...common} data-id="element-51" data-mp="12a5ng4ik9yct">
        <ellipse cx="24" cy="24" rx="21" ry="9" transform="rotate(-24 24 24)" data-id="element-52" data-mp="129nkkp1e8lvx" />
        <circle cx="42" cy="17" r="3" fill={color} stroke="none" data-id="element-53" data-mp="1q09gxj21chq3" />
      </svg>;
  }
  if (kind === 'proton') {
    return <svg {...common} data-id="element-54" data-mp="1utubt28c0vl2">
        <circle cx="24" cy="24" r="11" data-id="element-55" data-mp="24nhtan72yt3n" />
        <path d="M24 18v12M18 24h12" strokeLinecap="round" data-id="element-56" data-mp="24mglcrld2mq5" />
      </svg>;
  }
  return <svg {...common} data-id="element-57" data-mp="1zpygzsty0qec">
      <path d="M12 34 24 14l14 12" strokeLinecap="round" strokeLinejoin="round" data-id="element-58" data-mp="1zpga829nmts6" />
      <circle cx="12" cy="34" r="4" data-id="element-59" data-mp="1vlqt1xvb7eh6" />
      <circle cx="24" cy="14" r="4" data-id="element-60" data-mp="1vowgz9s7cabb" />
      <circle cx="38" cy="26" r="4" data-id="element-61" data-mp="1qorwl8o6avft" />
    </svg>;
}
export function ChemistryParticleField() {
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const count = isMobile ? 6 : 13;
  const particles = useMemo(() => buildParticles(count, isMobile), [count, isMobile]);
  return <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true" role="presentation" data-id="element-62" data-mp="1bzvtlq0z48m2">
      {particles.map(p => {
      if (prefersReducedMotion) {
        return <div key={p.id} className="absolute" style={{
          left: `${p.startX}vw`,
          top: `${p.id * 13 % 90}vh`,
          opacity: p.opacity * 0.7
        }} data-id="element-63" data-mp="130h0sjxc3lye">
              <ParticleGlyph kind={p.kind} size={p.size} color={p.color} data-id="element-64" data-mp="3ekoo8f5c265g" />
            </div>;
      }
      return <motion.div key={p.id} className="absolute left-0 top-0 will-change-transform" style={{
        opacity: p.opacity
      }} initial={false} animate={{
        x: [`${p.startX}vw`, `${p.startX}vw`, `${p.endX}vw`, `${p.endX}vw`],
        y: ['-12vh', '40vh', '68vh', '112vh'],
        rotate: [0, 18 * p.spin, 32 * p.spin, 44 * p.spin]
      }} transition={{
        duration: p.duration,
        delay: p.delay,
        times: [0, 0.45, 0.7, 1],
        ease: 'linear',
        repeat: Infinity
      }} data-id="element-65" data-mp="3jlm0yc7kq7zk">
            <ParticleGlyph kind={p.kind} size={p.size} color={p.color} data-id="element-66" data-mp="2c6r2ogfl6rsp" />
          </motion.div>;
    })}
    </div>;
}